'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { io as ioClient, Socket } from 'socket.io-client'
import { nanoid } from 'nanoid'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'
import type {
  Book as BookType,
  Chapter as ChapterType,
  Note as NoteType,
} from '@/lib/types'
import {
  Book as BookIcon,
  Cloud,
  CloudOff,
  Loader2,
  Plus,
  Trash2,
  Download,
  StickyNote,
  ChevronLeft,
  MoreVertical,
  Pencil,
  FileText,
  Menu as MenuIcon,
} from 'lucide-react'

const DEVICE_ID_KEY = 'writer_device_id'

function getDeviceId(): string {
  if (typeof window === 'undefined') return 'ssr'
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) {
    id = nanoid(12)
    localStorage.setItem(DEVICE_ID_KEY, id)
  }
  return id
}

type SyncState = 'idle' | 'saving' | 'saved' | 'error' | 'offline'

export default function WriterApp() {
  const [book, setBook] = useState<BookType | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftContent, setDraftContent] = useState('')
  const [notes, setNotes] = useState<NoteType[]>([])
  const [syncState, setSyncState] = useState<SyncState>('idle')
  const [wsConnected, setWsConnected] = useState(false)
  const [showSidebar, setShowSidebar] = useState(true)
  const [showNotes, setShowNotes] = useState(true)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [editingBookTitle, setEditingBookTitle] = useState(false)

  const deviceId = useMemo(() => (typeof window !== 'undefined' ? getDeviceId() : ''), [])
  const socketRef = useRef<Socket | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const noteSaveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const selectedIdRef = useRef<string | null>(null)
  selectedIdRef.current = selectedId

  // Load book on mount
  useEffect(() => {
    ;(async () => {
      try {
        const r = await fetch('/api/book')
        if (!r.ok) throw new Error('fetch failed')
        const data = await r.json()
        setBook(data.book)
        const first = data.book?.chapters?.[0]
        if (first) {
          setSelectedId(first.id)
          setDraftTitle(first.title)
          setDraftContent(first.content)
          setNotes(first.notes || [])
        }
      } catch (e) {
        console.error(e)
        setSyncState('error')
        toast.error('Erro ao carregar o livro')
      }
    })()
  }, [])

  // WebSocket connection
  useEffect(() => {
    if (!deviceId) return
    const wsUrl = process.env.NEXT_PUBLIC_WS_URL || 'ws://2.25.158.60:3011'
    const socket = ioClient(wsUrl, {
      transports: ['websocket', 'polling'],
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 10000,
    })
    socketRef.current = socket

    socket.on('connect', () => {
      setWsConnected(true)
      setSyncState((s) => (s === 'error' || s === 'offline' ? 'idle' : s))
      socket.emit('join', { role: 'web', deviceId })
    })
    socket.on('disconnect', () => {
      setWsConnected(false)
      setSyncState('offline')
    })
    socket.on('connect_error', () => {
      setWsConnected(false)
      setSyncState('offline')
    })

    // Real-time events from other devices
    socket.on('chapter-update', (data: { chapterId: string; title?: string; content?: string; deviceId: string }) => {
      if (data.deviceId === deviceId) return // ignore my own echo
      setBook((b) => {
        if (!b) return b
        return {
          ...b,
          chapters: b.chapters.map((c) =>
            c.id === data.chapterId
              ? {
                  ...c,
                  title: typeof data.title === 'string' ? data.title : c.title,
                  content: typeof data.content === 'string' ? data.content : c.content,
                }
              : c
          ),
        }
      })
      if (selectedIdRef.current === data.chapterId) {
        if (typeof data.title === 'string') setDraftTitle(data.title)
        if (typeof data.content === 'string') setDraftContent(data.content)
      }
    })

    socket.on('chapter-saved', (data: { chapterId: string; savedAt: string; deviceId: string }) => {
      if (data.deviceId === deviceId) return
      // Refresh this chapter from server to get canonical version
      ;(async () => {
        try {
          const r = await fetch(`/api/chapters/${data.chapterId}`)
          if (!r.ok) return
          const d = await r.json()
          const ch: ChapterType = d.chapter
          setBook((b) => {
            if (!b) return b
            return { ...b, chapters: b.chapters.map((c) => (c.id === ch.id ? ch : c)) }
          })
          if (selectedIdRef.current === ch.id) {
            setDraftTitle(ch.title)
            setDraftContent(ch.content)
            setNotes(ch.notes || [])
          }
        } catch {}
      })()
    })

    socket.on('chapter-created', (data: { chapterId: string; deviceId: string }) => {
      if (data.deviceId === deviceId) return
      ;(async () => {
        try {
          const r = await fetch(`/api/chapters/${data.chapterId}`)
          if (!r.ok) return
          const d = await r.json()
          const ch: ChapterType = d.chapter
          setBook((b) => (b ? { ...b, chapters: [...b.chapters, ch] } : b))
        } catch {}
      })()
    })

    socket.on('chapter-deleted', (data: { chapterId: string; deviceId: string }) => {
      if (data.deviceId === deviceId) return
      setBook((b) => {
        if (!b) return b
        const chapters = b.chapters.filter((c) => c.id !== data.chapterId)
        if (selectedIdRef.current === data.chapterId) {
          const next = chapters[0] || null
          setSelectedId(next?.id || null)
          if (next) {
            setDraftTitle(next.title)
            setDraftContent(next.content)
            setNotes(next.notes || [])
          }
        }
        return { ...b, chapters }
      })
    })

    socket.on('note-saved', (data: { noteId: string; chapterId: string; deviceId: string }) => {
      if (data.deviceId === deviceId) return
      ;(async () => {
        try {
          const r = await fetch(`/api/chapters/${data.chapterId}`)
          if (!r.ok) return
          const d = await r.json()
          const ch: ChapterType = d.chapter
          setNotes(ch.notes || [])
          setBook((b) => {
            if (!b) return b
            return { ...b, chapters: b.chapters.map((c) => (c.id === ch.id ? ch : c)) }
          })
        } catch {}
      })()
    })

    socket.on('note-created', (data: { noteId: string; chapterId: string; deviceId: string }) => {
      if (data.deviceId === deviceId) return
      ;(async () => {
        try {
          const r = await fetch(`/api/chapters/${data.chapterId}`)
          if (!r.ok) return
          const d = await r.json()
          const ch: ChapterType = d.chapter
          if (selectedIdRef.current === data.chapterId) setNotes(ch.notes || [])
          setBook((b) => (b ? { ...b, chapters: b.chapters.map((c) => (c.id === ch.id ? ch : c)) } : b))
        } catch {}
      })()
    })

    socket.on('note-deleted', (data: { noteId: string; chapterId: string; deviceId: string }) => {
      if (data.deviceId === deviceId) return
      setNotes((arr) => arr.filter((n) => n.id !== data.noteId))
      setBook((b) => {
        if (!b) return b
        return {
          ...b,
          chapters: b.chapters.map((c) =>
            c.id === data.chapterId ? { ...c, notes: c.notes.filter((n) => n.id !== data.noteId) } : c
          ),
        }
      })
    })

    // Heartbeat
    const hb = setInterval(() => socket.emit('heartbeat', { deviceId }), 30000)

    return () => {
      clearInterval(hb)
      socket.disconnect()
    }
  }, [deviceId])

  // ----- Actions -----

  const selectChapter = useCallback(
    (ch: ChapterType) => {
      setSelectedId(ch.id)
      setDraftTitle(ch.title)
      setDraftContent(ch.content)
      setNotes(ch.notes || [])
    },
    []
  )

  const createChapter = useCallback(async () => {
    try {
      const r = await fetch('/api/chapters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!r.ok) throw new Error()
      const d = await r.json()
      const ch: ChapterType = d.chapter
      setBook((b) => (b ? { ...b, chapters: [...b.chapters, ch] } : b))
      setSelectedId(ch.id)
      setDraftTitle(ch.title)
      setDraftContent(ch.content)
      setNotes([])
      socketRef.current?.emit('chapter-created', { chapterId: ch.id, deviceId })
      toast.success(`Capítulo "${ch.title}" criado`)
    } catch {
      toast.error('Erro ao criar capítulo')
    }
  }, [deviceId])

  const deleteChapter = useCallback(
    async (id: string) => {
      try {
        const r = await fetch(`/api/chapters/${id}`, { method: 'DELETE' })
        if (!r.ok) throw new Error()
        setBook((b) => {
          if (!b) return b
          const chapters = b.chapters.filter((c) => c.id !== id)
          if (selectedIdRef.current === id) {
            const next = chapters[0] || null
            setSelectedId(next?.id || null)
            if (next) {
              setDraftTitle(next.title)
              setDraftContent(next.content)
              setNotes(next.notes || [])
            } else {
              setDraftTitle('')
              setDraftContent('')
              setNotes([])
            }
          }
          return { ...b, chapters }
        })
        socketRef.current?.emit('chapter-deleted', { chapterId: id, deviceId })
        toast.success('Capítulo removido')
      } catch {
        toast.error('Erro ao remover capítulo')
      }
    },
    [deviceId]
  )

  // Auto-save on title/content change with debounce 1.5s
  const scheduleSave = useCallback(
    (title: string, content: string) => {
      setSyncState('saving')
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(async () => {
        const id = selectedIdRef.current
        if (!id) return
        try {
          // Broadcast in-progress update via WS immediately (low latency live preview)
          socketRef.current?.emit('chapter-update', { chapterId: id, title, content, deviceId })
          const r = await fetch(`/api/chapters/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title, content }),
          })
          if (!r.ok) throw new Error()
          const d = await r.json()
          const ch: ChapterType = d.chapter
          setBook((b) => {
            if (!b) return b
            return { ...b, chapters: b.chapters.map((c) => (c.id === ch.id ? ch : c)) }
          })
          setSyncState('saved')
          setLastSavedAt(new Date())
          socketRef.current?.emit('chapter-saved', { chapterId: id, savedAt: new Date().toISOString(), deviceId })
        } catch {
          setSyncState('error')
          toast.error('Falha ao salvar — tentando novamente em 5s')
          // retry
          setTimeout(() => scheduleSave(title, content), 5000)
        }
      }, 1500)
    },
    [deviceId]
  )

  const onTitleChange = useCallback(
    (v: string) => {
      setDraftTitle(v)
      if (selectedId) scheduleSave(v, draftContent)
    },
    [selectedId, draftContent, scheduleSave]
  )

  const onContentChange = useCallback(
    (v: string) => {
      setDraftContent(v)
      if (selectedId) scheduleSave(draftTitle, v)
    },
    [selectedId, draftTitle, scheduleSave]
  )

  // ----- Notes -----

  const createNote = useCallback(async () => {
    if (!selectedId) return
    try {
      const r = await fetch(`/api/chapters/${selectedId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: '', color: 'yellow' }),
      })
      if (!r.ok) throw new Error()
      const d = await r.json()
      const n: NoteType = d.note
      setNotes((arr) => [...arr, n])
      setBook((b) => {
        if (!b) return b
        return {
          ...b,
          chapters: b.chapters.map((c) => (c.id === selectedId ? { ...c, notes: [...c.notes, n] } : c)),
        }
      })
      socketRef.current?.emit('note-created', { noteId: n.id, chapterId: selectedId, deviceId })
    } catch {
      toast.error('Erro ao criar anotação')
    }
  }, [selectedId, deviceId])

  const updateNote = useCallback(
    (id: string, content: string) => {
      setNotes((arr) => arr.map((n) => (n.id === id ? { ...n, content } : n)))
      // debounce save per note
      const existing = noteSaveTimersRef.current.get(id)
      if (existing) clearTimeout(existing)
      const timer = setTimeout(async () => {
        try {
          const r = await fetch(`/api/notes/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content }),
          })
          if (!r.ok) throw new Error()
          if (selectedIdRef.current) {
            socketRef.current?.emit('note-saved', { noteId: id, chapterId: selectedIdRef.current, deviceId })
          }
        } catch {
          toast.error('Erro ao salvar anotação')
        }
      }, 800)
      noteSaveTimersRef.current.set(id, timer)
    },
    [deviceId]
  )

  const deleteNote = useCallback(
    async (id: string) => {
      const chapterId = selectedId
      setNotes((arr) => arr.filter((n) => n.id !== id))
      try {
        await fetch(`/api/notes/${id}`, { method: 'DELETE' })
        if (chapterId) socketRef.current?.emit('note-deleted', { noteId: id, chapterId, deviceId })
      } catch {}
    },
    [selectedId, deviceId]
  )

  // ----- Book title edit -----

  const saveBookTitle = useCallback(
    async (title: string) => {
      if (!book) return
      try {
        const r = await fetch('/api/book', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title }),
        })
        if (!r.ok) throw new Error()
        const d = await r.json()
        setBook((b) => (b ? { ...b, title: d.book.title } : b))
      } catch {
        toast.error('Erro ao renomear livro')
      }
    },
    [book]
  )

  const exportMarkdown = useCallback(() => {
    window.open('/api/export', '_blank')
  }, [])

  // ----- Render -----

  const selectedChapter = useMemo(
    () => book?.chapters.find((c) => c.id === selectedId) || null,
    [book, selectedId]
  )

  const wordCount = useMemo(() => {
    if (!draftContent.trim()) return 0
    return draftContent.trim().split(/\s+/).length
  }, [draftContent])

  const syncDotColor = useMemo(() => {
    if (!wsConnected) return 'bg-red-500'
    if (syncState === 'saving') return 'bg-yellow-500'
    if (syncState === 'error' || syncState === 'offline') return 'bg-red-500'
    return 'bg-green-500'
  }, [wsConnected, syncState])

  const syncLabel = useMemo(() => {
    if (!wsConnected) return 'Offline'
    if (syncState === 'saving') return 'Salvando…'
    if (syncState === 'error') return 'Erro ao salvar'
    if (syncState === 'saved' && lastSavedAt)
      return `Salvo às ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    return 'Tudo salvo'
  }, [wsConnected, syncState, lastSavedAt])

  return (
    <div className="h-screen flex flex-col bg-stone-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      {/* Top bar */}
      <header className="flex-shrink-0 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-2.5 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setShowSidebar((s) => !s)}
          aria-label="Toggle sidebar"
        >
          <MenuIcon className="w-5 h-5" />
        </Button>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <BookIcon className="w-4 h-4 text-amber-700 dark:text-amber-500 flex-shrink-0" />
          {editingBookTitle ? (
            <Input
              autoFocus
              defaultValue={book?.title || ''}
              className="h-7 max-w-xs"
              onBlur={(e) => {
                saveBookTitle(e.target.value)
                setEditingBookTitle(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveBookTitle((e.target as HTMLInputElement).value)
                  setEditingBookTitle(false)
                }
                if (e.key === 'Escape') setEditingBookTitle(false)
              }}
            />
          ) : (
            <button
              onClick={() => setEditingBookTitle(true)}
              className="font-semibold text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 px-2 py-1 rounded truncate"
            >
              {book?.title || 'Carregando…'}
            </button>
          )}
        </div>

        {/* Sync indicator */}
        <div className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400">
          <span
            className={`w-2.5 h-2.5 rounded-full ${syncDotColor} ${syncState === 'saving' ? 'animate-pulse' : ''}`}
            aria-label={syncLabel}
            title={syncLabel}
          />
          <span className="hidden sm:inline">{syncLabel}</span>
        </div>

        <Button variant="ghost" size="icon" onClick={exportMarkdown} title="Exportar Markdown">
          <Download className="w-4 h-4" />
        </Button>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - chapter cards */}
        <aside
          className={`${
            showSidebar ? 'w-72' : 'w-0'
          } md:w-72 flex-shrink-0 border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden transition-all`}
        >
          <div className="h-full flex flex-col">
            <div className="p-3 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Capítulos {book ? `· ${book.chapters.length}` : ''}
              </h2>
              <Button size="sm" variant="ghost" onClick={createChapter} title="Novo capítulo">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-2">
                {book?.chapters.map((ch, idx) => {
                  const preview = ch.content?.trim().slice(0, 80) || '_(vazio)_'
                  const words = ch.content?.trim() ? ch.content.trim().split(/\s+/).length : 0
                  return (
                    <Card
                      key={ch.id}
                      onClick={() => selectChapter(ch)}
                      className={`p-3 cursor-pointer transition-colors border ${
                        selectedId === ch.id
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700'
                          : 'border-zinc-200 dark:border-zinc-800 hover:border-amber-300 dark:hover:border-amber-700'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1">
                            <FileText className="w-3 h-3" />
                            <span>Cap. {idx + 1}</span>
                            <span className="text-zinc-400">·</span>
                            <span>{words} palavras</span>
                          </div>
                          <h3 className="text-sm font-semibold truncate">{ch.title || 'Sem título'}</h3>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2 mt-1">{preview}</p>
                          {ch.notes?.length > 0 && (
                            <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-500">
                              <StickyNote className="w-3 h-3" /> {ch.notes.length}
                            </div>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-3.5 h-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuLabel>Ações</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem
                                  onSelect={(e) => e.preventDefault()}
                                  className="text-red-600 dark:text-red-400"
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir capítulo?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser desfeita. O capítulo "{ch.title}" e todas as suas
                                    anotações serão removidos permanentemente.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteChapter(ch.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </Card>
                  )
                })}
                {!book && (
                  <div className="text-center text-zinc-500 text-xs py-8">
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </aside>

        {/* Editor */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {selectedChapter ? (
            <>
              <div className="flex-shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200 dark:border-zinc-800">
                <Input
                  value={draftTitle}
                  onChange={(e) => onTitleChange(e.target.value)}
                  placeholder="Título do capítulo"
                  className="text-2xl font-bold border-0 px-0 bg-transparent focus-visible:ring-0"
                />
                <div className="flex items-center justify-between mt-2 text-xs text-zinc-500">
                  <span>{wordCount} palavras · {draftContent.length} caracteres</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowNotes((s) => !s)}
                    className="md:hidden"
                  >
                    <StickyNote className="w-4 h-4 mr-1" /> {notes.length}
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <Textarea
                  value={draftContent}
                  onChange={(e) => onContentChange(e.target.value)}
                  placeholder="Comece a escrever…"
                  className="w-full h-full resize-none border-0 rounded-none focus-visible:ring-0 text-base leading-relaxed font-serif bg-transparent p-6 md:p-10"
                  style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <BookIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Selecione um capítulo ou crie um novo</p>
                <Button size="sm" variant="outline" className="mt-4" onClick={createChapter}>
                  <Plus className="w-4 h-4 mr-1" /> Novo capítulo
                </Button>
              </div>
            </div>
          )}
        </main>

        {/* Notes panel */}
        <aside
          className={`${
            showNotes ? 'w-80' : 'w-0'
          } md:w-80 flex-shrink-0 border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden transition-all`}
        >
          <div className="h-full flex flex-col">
            <div className="p-3 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Anotações {notes.length > 0 ? `· ${notes.length}` : ''}
              </h2>
              <Button size="sm" variant="ghost" onClick={createNote} title="Nova anotação">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-3">
                {notes.map((n) => (
                  <Card
                    key={n.id}
                    className="p-3 border-l-4 bg-amber-50 dark:bg-amber-950/30 border-l-amber-400"
                  >
                    <Textarea
                      value={n.content}
                      onChange={(e) => updateNote(n.id, e.target.value)}
                      placeholder="Escreva uma anotação…"
                      className="bg-transparent border-0 resize-none focus-visible:ring-0 text-sm p-0 min-h-[60px]"
                    />
                    <div className="flex justify-end mt-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-zinc-500 hover:text-red-600"
                        onClick={() => deleteNote(n.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </Card>
                ))}
                {notes.length === 0 && (
                  <div className="text-center text-zinc-500 text-xs py-6">
                    <StickyNote className="w-6 h-6 mx-auto mb-2 opacity-30" />
                    <p>Sem anotações neste capítulo</p>
                    <Button size="sm" variant="ghost" className="mt-2 text-xs" onClick={createNote}>
                      <Plus className="w-3 h-3 mr-1" /> Adicionar
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </aside>
      </div>
    </div>
  )
}
